import React, { Suspense, useRef, useEffect } from 'react';
import ensureSkeletonHidden from './shared/util/ensure-skeleton-hidden';
import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';
// import 'antd/dist/reset.css'; // Import the global styles


import MainNavigation from './shared/components/Navigation/MainNavigation';
import MainFooter from './shared/components/Footer/MainFooter';
import { AuthContext } from './shared/context/auth-context';
import { CategoriesContext } from './shared/context/categories-context';
import { StoreSectionsContext } from './shared/context/store-sections-context';
import { StoresContext } from './shared/context/stores-context';
import { StoreDetailsContext } from './shared/context/store-details-context';
import { StoreSettingsContext } from './shared/context/store-settings-context';
import LanguageProvider from './shared/context/language-provider';
import { ThemeContext } from './shared/context/theme-context';
import { SectionsContext } from './shared/context/sections-context';
import { FeedbacksContext } from './shared/context/feedbacks-context';
import { ShoppingCartContext } from './shared/context/shopping-cart-context';
import useAuth from './shared/context/auth-hook';
import useCategories from './shared/context/categories-hook';
import useStoreSections from './shared/context/store-sections-hook';
import useStoreDetails from './shared/context/store-details-hook';
import useStoreSettings from './shared/context/store-settings-hook';
import useStores from './shared/context/stores-hook';
import useTheme from './shared/context/theme-hook';
import useFeedbacks from './shared/context/feedbacks-hook';
import useCart from './shared/context/shopping-cart-hook';
import ScrollToTop from './shared/util/scroll';
import WhatsAppIcon from './shared/components/Whatsapp/whatsapp';
import LoadingSpinner from './shared/components/UIElements/LoadingSpinner';
import useSections from './shared/context/sections-hook';

const Menu = React.lazy(() => import('./Menu/pages/menu'));
const AdminHome = React.lazy(() => import('./admin/pages/adminHome'));
const AddStore = React.lazy(() => import('./admin/components/addStore'));
const StoreDetails = React.lazy(() => import('./admin/components/storeDetails'));
const Auth = React.lazy(() => import('./User/pages/Auth'));
const EditInfo = React.lazy(() => import('./admin/components/editInfo'));
const AdminStoreTheme = React.lazy(() => import('./admin/components/storeTheme'));
const StoreSettings = React.lazy(() => import('./admin/components/storeSettings'));
const StoreEditInfo = React.lazy(() => import('./store/components/store-editInfo'));
const StoreEditDetails = React.lazy(() => import('./store/components/store-editDetails'));
const StoreEditTheme = React.lazy(() => import('./store/components/store-editTheme'));
const Contact = React.lazy(() => import('./Contact/pages/contact'));
const About = React.lazy(() => import('./About/pages/about'));
const QRCodeComponent = React.lazy(() => import('./QR-Code/pages/qrcode'));
const Welcome = React.lazy(() => import('./Welcome/pages/welcome'));

// const Signup = React.lazy(() => import('./User/pages/Signup'));

const App = () => {
  const { userId, login, logout, updateAuth, token, role, username, name, image, email, phone, isPaymentDue } = useAuth();

  const {
    sections,
    updateSections,
    getSectionByName,
    isUpdatedSection,
    fetchSections,
    reloadSections,
    HideSectionsSkeleton,
    sectionError,
    setHideSectionsSkeleton,
    removeSections,
  } = useSections();

  const {
    theme,
    setTheme,
    isUpdatedTheme,
    fetchTheme,
    reloadTheme,
    HideThemeSkeleton,
    ThemeError,
    removeTheme,
    setHideThemeSkeleton,
    updateDOMColors,
  } = useTheme({ role: role });

  const {
    storeDetails,
    fetchStoreDetails,
    reloadStoreDetails,
    HideStoreDetailsSkeleton,
    isUpdatedStoreDetails,
    StoreDetailsError,
    removeStoreDetails,
    setHideStoreDetailsSkeleton,
    storeUN,
    setStoreUN,
  } = useStoreDetails({ role: role });

  const {
    storeSettings,
    fetchStoreSettings,
    reloadStoreSettings,
    HideStoreSettingsSkeleton,
    isUpdatedStoreSettings,
    StoreSettingsError,
    removeStoreSettings,
    setHideStoreSettingsSkeleton,
    settingsStoreUsername,
  } = useStoreSettings({ role: role });

  const {
    cart,
    isAddToCard,
    addToCart,
    updateQuantity,
    clearCart,
    getTotal,
    removeFromCart,
    getQuantity,
  } = useCart();

  const {
    feedbacks,
    updateFeedbacks,
    isUpdatedFeedbacks,
    fetchFeedbacks,
    reloadFeedbacks,
    HideFeedbacksSkeleton,
    isEmptyFeedbacks,
    FeedbackDeletedHandler,
    FeedbacksError,
    adminFetchFeedbacks,
    setHideFeedbacksSkeleton,
    removeFeedbacks,
  } = useFeedbacks();

  const {
    categories,
    updateCategories,
    isUpdatedCategories,
    fetchCategories,
    reloadCategories,
    HideCategoriesSkeleton,
    isEmptyItems,
    CategoryDeletedHandler,
    ProductDeletedHandler,
    categoriesError,
    setHideCategoriesSkeleton,
    removeCategories
  } = useCategories();

  const {
    storeSections,
    updateStoreSections,
    isUpdatedStoreSections,
    fetchStoreSections,
    reloadStoreSections,
    HideStoreSectionsSkeleton,
    StoreSectionDeletedHandler,
    storeSectionsError,
    setHideStoreSectionsSkeleton,
    removeStoreSections
  } = useStoreSections();

  const {
    stores,
    isUpdatedStores,
    fetchStores,
    fetchStore,
    updateStore,
    HideStoresSkeleton,
    setHideStoresSkeleton,
    removeStores,
    StoresError,
    storeListPosition,
    handleStoreListPosition,
    setStoreListPosition,
    fetchUnregisteredStores,
    usersType,
    setUsersType,
    removeReq,
  } = useStores();

  const previousRoleRef = useRef(role);
  const previousRoleForSkeletonRef = useRef(role);

  useEffect(() => {
    if (previousRoleRef.current === 'admin' && role === 'viewer') {
      removeStores();
    }
    previousRoleRef.current = role;
  }, [role, removeStores]);

  useEffect(() => {
    if (previousRoleForSkeletonRef.current === role) {
      return;
    }
    previousRoleForSkeletonRef.current = role;

    ensureSkeletonHidden(isUpdatedTheme, HideThemeSkeleton, setHideThemeSkeleton);
    ensureSkeletonHidden(isUpdatedStoreDetails, HideStoreDetailsSkeleton, setHideStoreDetailsSkeleton);
    ensureSkeletonHidden(isUpdatedStoreSettings, HideStoreSettingsSkeleton, setHideStoreSettingsSkeleton);
    ensureSkeletonHidden(isUpdatedCategories, HideCategoriesSkeleton, setHideCategoriesSkeleton);
    ensureSkeletonHidden(isUpdatedStoreSections, HideStoreSectionsSkeleton, setHideStoreSectionsSkeleton);
    ensureSkeletonHidden(isUpdatedSection, HideSectionsSkeleton, setHideSectionsSkeleton);
    ensureSkeletonHidden(isUpdatedFeedbacks, HideFeedbacksSkeleton, setHideFeedbacksSkeleton);
  }, [
    role,
    isUpdatedTheme,
    HideThemeSkeleton,
    setHideThemeSkeleton,
    isUpdatedStoreDetails,
    HideStoreDetailsSkeleton,
    setHideStoreDetailsSkeleton,
    isUpdatedStoreSettings,
    HideStoreSettingsSkeleton,
    setHideStoreSettingsSkeleton,
    isUpdatedCategories,
    HideCategoriesSkeleton,
    setHideCategoriesSkeleton,
    isUpdatedStoreSections,
    HideStoreSectionsSkeleton,
    setHideStoreSectionsSkeleton,
    isUpdatedSection,
    HideSectionsSkeleton,
    setHideSectionsSkeleton,
    isUpdatedFeedbacks,
    HideFeedbacksSkeleton,
    setHideFeedbacksSkeleton,
  ]);

  let routes;
  if (token && role === 'store') {
    routes = (
      <Switch>
        <Route
          path={`/store/:storeUsername`}
          exact
          render={({ match }) => {
            const { storeUsername } = match.params;
            return storeUsername === username ? <Menu /> : <Redirect to={`/store/${username}`} />;
          }}
        />
        <Route
          path={`/store/contact/:storeUsername`}
          exact
          render={({ match }) => {
            const { storeUsername } = match.params;
            return storeUsername === username ? <Contact /> : <Redirect to={`/store/contact/${username}`} />;
          }}
        />
        <Route
          path={`/store/about/:storeUsername`}
          exact
          render={({ match }) => {
            const { storeUsername } = match.params;
            return storeUsername === username ? <About /> : <Redirect to={`/store/about/${username}`} />;
          }}
        />
        <Route
          path={`/store/qr/:storeUsername`}
          exact
          render={({ match }) => {
            const { storeUsername } = match.params;
            return storeUsername === username ? <QRCodeComponent /> : <Redirect to={`/store/qr/${username}`} />;
          }}
        />
        <Route path="/store/profile/edit" exact><StoreEditInfo /></Route>
        <Route path="/store/details/edit" exact><StoreEditDetails /></Route>
        <Route path="/store/theme/edit" exact><StoreEditTheme /></Route>
        <Redirect to={`/store/${username}`} />
      </Switch>
    );
  } else if (token && role === 'admin') {
    routes = (
      <Switch>
        <Route path="/administration/home" exact><AdminHome /></Route>
        <Route path="/administration/store/add" exact><AddStore /></Route>
        <Route path="/administration/store/details" exact><StoreDetails /></Route>
        <Route path="/administration/store/profile" exact><EditInfo /></Route>
        <Route path="/administration/store/theme" exact><AdminStoreTheme /></Route>
        <Route path="/administration/store/qr/:storeUsername" exact><QRCodeComponent /></Route>
        <Route path="/administration/store/settings/:storeUsername" exact><StoreSettings /></Route>
        <Route path="/store/:storeUsername" exact><Menu /></Route>
        <Route path="/store/contact/:storeUsername" exact><Contact /></Route>
        <Route path="/store/about/:storeUsername" exact><About /></Route>
        <Redirect to="/administration/home" />
      </Switch>
    );
  } else {
    routes = (
      <Switch>
        <Route path="/" exact><Welcome /></Route>
        <Route path="/login" exact><Auth /></Route>
        <Route path="/store/:storeUsername" exact><Menu /></Route>
        <Route path="/store/contact/:storeUsername" exact><Contact /></Route>
        <Route path="/store/about/:storeUsername" exact><About /></Route>
        <Redirect to="/" />
      </Switch>
    );
  }

  return (
    <React.Fragment>
      {(storeDetails?.whatsapp) &&
        <WhatsAppIcon
          whatsapp={storeDetails.whatsapp}
        />
      }
      <AuthContext.Provider value={{
        isLoggedIn: !!token,
        userId: userId,
        role: role,
        token: token,
        username: username,
        name: name,
        image: image,
        email: email,
        phone: phone,
        login: login,
        logout: logout,
        updateAuth: updateAuth
      }}>
        <SectionsContext.Provider value={{
          sections,
          updateSections,
          fetchSections,
          reloadSections,
          getSectionByName,
          isUpdatedSection,
          sectionError,
          HideSectionsSkeleton,
          setHideSectionsSkeleton,
          removeSections,
        }}>
          <StoreSectionsContext.Provider value={{
            storeSections,
            updateStoreSections,
            fetchStoreSections,
            reloadStoreSections,
            StoreSectionDeletedHandler,
            HideStoreSectionsSkeleton,
            isUpdatedStoreSections,
            storeSectionsError,
            setHideStoreSectionsSkeleton,
            removeStoreSections
          }}>
            <CategoriesContext.Provider value={{
              categories,
              updateCategories,
              isEmptyItems,
              fetchCategories,
              reloadCategories,
              CategoryDeletedHandler,
              ProductDeletedHandler,
              HideCategoriesSkeleton,
              isUpdatedCategories,
              categoriesError,
              setHideCategoriesSkeleton,
              removeCategories
            }}>

              <StoreDetailsContext.Provider value={{
                storeDetails,
                fetchStoreDetails,
                reloadStoreDetails,
                HideStoreDetailsSkeleton,
                isUpdatedStoreDetails,
                StoreDetailsError,
                removeStoreDetails,
                setHideStoreDetailsSkeleton,
                storeUN,
                setStoreUN
              }}>
                <StoreSettingsContext.Provider value={{
                  storeSettings,
                  fetchStoreSettings,
                  reloadStoreSettings,
                  HideStoreSettingsSkeleton,
                  isUpdatedStoreSettings,
                  StoreSettingsError,
                  removeStoreSettings,
                  setHideStoreSettingsSkeleton,
                  settingsStoreUsername,
                }}>
                  <StoresContext.Provider value={{
                    stores,
                    fetchStores,
                    fetchStore,
                    updateStore,
                    HideStoresSkeleton,
                    setHideStoresSkeleton,
                    isUpdatedStores,
                    removeStores,
                    StoresError,
                    storeListPosition,
                    handleStoreListPosition,
                    setStoreListPosition,
                    fetchUnregisteredStores,
                    usersType,
                    setUsersType,
                    removeReq,
                  }}>
                    <ThemeContext.Provider value={{
                      theme,
                      setTheme,
                      isUpdatedTheme,
                      fetchTheme,
                      reloadTheme,
                      HideThemeSkeleton,
                      ThemeError,
                      removeTheme,
                      setHideThemeSkeleton,
                      updateDOMColors,
                    }}>
                      <FeedbacksContext.Provider value={{
                        feedbacks,
                        updateFeedbacks,
                        isUpdatedFeedbacks,
                        fetchFeedbacks,
                        reloadFeedbacks,
                        HideFeedbacksSkeleton,
                        isEmptyFeedbacks,
                        FeedbackDeletedHandler,
                        FeedbacksError,
                        adminFetchFeedbacks,
                        setHideFeedbacksSkeleton,
                        removeFeedbacks,
                      }}>
                        <ShoppingCartContext.Provider value={{
                          cart,
                          isAddToCard,
                          addToCart,
                          updateQuantity,
                          clearCart,
                          getTotal,
                          removeFromCart,
                          getQuantity,
                        }}>
                          <Router>
                            <LanguageProvider
                              storeSettings={storeSettings}
                              isStoreSettingsReady={isUpdatedStoreSettings}
                              settingsStoreUsername={settingsStoreUsername}
                              isPaymentDue={isPaymentDue}
                              token={token}
                              role={role}
                            >
                              <ScrollToTop />
                              <MainNavigation />
                              <Suspense fallback={<LoadingSpinner asOverlay />}>
                                <main id="main-container" className='main-container'>
                                  {
                                    routes
                                  }
                                </main>
                              </Suspense>
                              <MainFooter />
                            </LanguageProvider>
                          </Router>
                        </ShoppingCartContext.Provider>
                      </FeedbacksContext.Provider>
                    </ThemeContext.Provider>
                  </StoresContext.Provider>
                </StoreSettingsContext.Provider>
              </StoreDetailsContext.Provider>
            </CategoriesContext.Provider>
          </StoreSectionsContext.Provider>
        </SectionsContext.Provider>
      </AuthContext.Provider>
    </React.Fragment>
  );
}

export default App;
